import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Subject, Observable, BehaviorSubject } from 'rxjs';
import { tap, map } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { Document } from '@classes/document/document';
import * as FileSaver from 'file-saver';
import { UiService } from './ui.service';
import { PaginationRequest } from '@classes/pagination/pagination-request';
import { Pagination } from '@classes/pagination/pagination';
import { AddDocumentRequest } from '@classes/document/add-document-request';
import { UserDocument } from '@classes/document/user-document';


@Injectable({
  providedIn: 'root'
})
export class DocumentsService {
  paginationSubject = new BehaviorSubject<Pagination>(null);

  documentsListChanged = new Subject<Document[]>();
  private baseUrl: string = environment.apiUrl + 'documents';
  private documents: Document[];

  private lastPaginationRequest: PaginationRequest;

  constructor(
    private http: HttpClient,
    private uiSrv: UiService
  ) {
    this.lastPaginationRequest = new PaginationRequest();
  }

  public fetch(request: PaginationRequest = new PaginationRequest()): void {
    this.uiSrv.loadingStateChanged.next(true);
    this.lastPaginationRequest = request;
    let httpParams = this._createHttpParamsForGet(request);

    this.http.get(this.baseUrl, { params: httpParams, observe: 'response' })
      .subscribe((response: HttpResponse<Document[]>) => {
        this.uiSrv.loadingStateChanged.next(false);
        this.documents = response.body;
        const pagination: Pagination = JSON.parse(response.headers.get('X-Pagination'));

        this.documentsListChanged.next([...this.documents]);
        this.paginationSubject.next({ ...pagination });
      });
  }

  private _createHttpParamsForGet(request: PaginationRequest): HttpParams {
    let httpParams = new HttpParams();

    httpParams = this._createSortHttpParams(httpParams, request);
    httpParams = this._createPaginationHttpParams(httpParams, request);
    httpParams = this._createFilterHttpParams(httpParams, request);

    return httpParams;
  }

  private _createSortHttpParams(httpParams: HttpParams, { sort }: PaginationRequest): HttpParams {
    if (sort)
      httpParams = httpParams.append('sorts', sort);

    return httpParams;
  }

  private _createPaginationHttpParams(httpParams: HttpParams, { pageSize, page }: PaginationRequest): HttpParams {
    if (pageSize && page)
      httpParams = httpParams
        .append("pageSize", pageSize)
        .append("page", page);

    return httpParams;
  }

  private _createFilterHttpParams(httpParams: HttpParams, { query }: PaginationRequest): HttpParams {
    if (query) {
      const filtersQuery = [];

      if (query.fileName)
        filtersQuery.push(`fileName@=${query.fileName}`);

      if (query.clientName)
        filtersQuery.push(`clientName==${query.clientName}`);

      if (query.category)
        filtersQuery.push(`category==${query.category}`);

      if (filtersQuery.length > 0)
        httpParams = httpParams.append('filters', filtersQuery.join(','));
    }

    return httpParams;
  }

  public getUsersForDocument$({ id }: Document): Observable<UserDocument[]> {
    return this.http.get<UserDocument[]>(`${this.baseUrl}/${id}/users`);
  }

  public post$(request: AddDocumentRequest): Observable<Document> {
    return this.http.post<Document>(this.baseUrl, request)
      .pipe(
        tap(() => {
          this.fetch(this.lastPaginationRequest);
        }));
  }

  public markAsRead$({ id }: Document): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${id}/mark-as-read`, {}).pipe(
      tap(() => {
        this.fetch(this.lastPaginationRequest);
      })
    );
  }

  public delete$({ id }: Document): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`)
      .pipe(
        tap(() => {
          this.fetch(this.lastPaginationRequest);
        })
      );
  }

  public downloadFile$(id: string): Observable<Blob> {
    this.uiSrv.loadingStateChanged.next(true);
    return this.http.get(`${this.baseUrl}/${id}/file`, { observe: 'response', responseType: 'blob' })
      .pipe(map(res => this.saveFile(res)));
  }

  private saveFile(res: HttpResponse<Blob>): Blob {
    FileSaver.saveAs(res.body, res.headers.get('filename'));
    this.uiSrv.loadingStateChanged.next(false);

    return res.body;
  }
}
